import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/shared/Layout/Layout';
import Loading from '../../components/shared/Loading/Loading';
import Button from '../../components/shared/Button/Button';
import StatusBadge from '../../components/shared/StatusBadge/StatusBadge';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  ErrorMessage,
} from '../../components/shared/Form/Form.styles';
import SalesNav from '../../components/sales/SalesNav';
import { isVoided, paymentMethodLabel, saleStatus } from '../../components/sales/saleLabels';
import { listSales, listSellableActivities } from '../../services/salesApi';
import { listEmpresaActivities } from '../../services/empresaStaff';
import { StaffSessionError } from '../../services/staffApi';
import { formatMoney } from '../../utils/money';
import { formatCalendarDate, shiftCalendarDate, todayInMexico } from '../../utils/dates';
import useSalesAccess from './useSalesAccess';
import SalesGate from './SalesGate';
import SaleDetailModal from './SaleDetailModal';
import ExportModal from './ExportModal';
import { SalesContainer, Panel, IvaTag } from './Sales.styles';
import {
  FilterBar,
  PresetRow,
  SummaryGrid,
  SaleList,
  SaleRow,
  Pager,
  HeadRow,
  EmptyBox,
} from './SalesList.styles';

const PAGE_SIZE = 20;

const statusOptions = [
  { value: 'all', label: 'Todas' },
  { value: 'valid', label: 'Vigentes' },
  { value: 'voided', label: 'Anuladas' },
];

const SalesListScreen = ({ access }) => {
  const { mode, isStaff, staffAuth } = access;
  const { endSession } = staffAuth;
  const today = todayInMexico();

  const [filters, setFilters] = useState({ from: today, to: today, activityId: '', status: 'all' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null); // { sales, summary, total, totalPages }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleSessionError = useCallback((err) => {
    if (err instanceof StaffSessionError) {
      endSession(err.message);
      return true;
    }
    return false;
  }, [endSession]);

  // Actividades para el filtro: el dueño ve todas las suyas (incluidas las
  // inactivas, que pueden tener ventas); el operador solo las que puede vender.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = isStaff
          ? (await listSellableActivities(mode)).activities
          : await listEmpresaActivities();
        if (!cancelled) setActivities((list || []).map((a) => ({ id: a.id, title: a.title })));
      } catch (err) {
        if (!cancelled) handleSessionError(err);
        // sin la lista el filtro por actividad simplemente queda vacío
      }
    })();
    return () => { cancelled = true; };
  }, [isStaff, mode, handleSessionError]);

  const rangeInvalid = filters.from && filters.to && filters.from > filters.to;

  const load = useCallback(async () => {
    if (rangeInvalid) return;
    setLoading(true);
    setError('');
    try {
      const result = await listSales(mode, {
        from: filters.from,
        to: filters.to,
        basis: 'sale', // el corte de caja es por fecha de VENTA (hora de México)
        activityId: filters.activityId,
        status: filters.status,
        page,
        limit: PAGE_SIZE,
      });
      setData(result);
    } catch (err) {
      if (handleSessionError(err)) return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mode, filters, page, rangeInvalid, handleSessionError]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key) => (event) => {
    setPage(1);
    setFilters({ ...filters, [key]: event.target.value });
  };

  const applyPreset = (from, to) => {
    setPage(1);
    setFilters({ ...filters, from, to });
  };

  const preset = (() => {
    if (!filters.from && !filters.to) return 'all';
    if (filters.from === today && filters.to === today) return 'today';
    if (filters.to === today && filters.from === shiftCalendarDate(today, -6)) return 'week';
    return '';
  })();

  const sales = (data && data.sales) || [];
  const summary = data && data.summary;
  const totalPages = (data && data.totalPages) || 1;

  // La venta anulada cambió dentro del modal: se actualiza la fila y el resumen.
  const handleChanged = () => { load(); };

  return (
    <Layout headerType={access.headerType}>
      <SalesContainer>
        <HeadRow>
          <div>
            <h1>{isStaff ? 'Mis ventas' : 'Ventas en persona'}</h1>
            <p>
              {isStaff
                ? 'Las ventas que tú registraste.'
                : 'Todas las ventas en persona de tu empresa, con corte de caja y exportación.'}
            </p>
          </div>
          {!isStaff && (
            <Button variant="secondary" onClick={() => setExporting(true)}>Exportar CSV / Excel</Button>
          )}
        </HeadRow>
        <SalesNav mode={mode} />

        <Panel>
          <h2>Filtros</h2>
          <PresetRow role="group" aria-label="Fechas rápidas">
            <button type="button" aria-pressed={preset === 'today'} onClick={() => applyPreset(today, today)}>Hoy</button>
            <button type="button" aria-pressed={preset === 'week'} onClick={() => applyPreset(shiftCalendarDate(today, -6), today)}>Últimos 7 días</button>
            <button type="button" aria-pressed={preset === 'all'} onClick={() => applyPreset('', '')}>Todas las fechas</button>
          </PresetRow>
          <FilterBar>
            <FormGroup>
              <FormLabel htmlFor="f-from">Venta desde</FormLabel>
              <FormInput id="f-from" type="date" value={filters.from} onChange={setFilter('from')} />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="f-to">Venta hasta</FormLabel>
              <FormInput id="f-to" type="date" value={filters.to} onChange={setFilter('to')} />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="f-activity">Actividad</FormLabel>
              <FormSelect id="f-activity" value={filters.activityId} onChange={setFilter('activityId')}>
                <option value="">Todas</option>
                {activities.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
              </FormSelect>
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="f-status">Estado</FormLabel>
              <FormSelect id="f-status" value={filters.status} onChange={setFilter('status')}>
                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </FormSelect>
            </FormGroup>
          </FilterBar>
          {rangeInvalid && <ErrorMessage role="alert">La fecha inicial no puede ser posterior a la final.</ErrorMessage>}
        </Panel>

        <Panel>
          <h2>Corte de caja</h2>
          {summary ? (
            <SummaryGrid>
              <div className="stat"><div className="value">{summary.sales}</div><div className="label">Ventas</div></div>
              <div className="stat"><div className="value">{summary.people}</div><div className="label">Personas</div></div>
              <div className="stat">
                <div className="value">{formatMoney(summary.amount)}</div>
                <div className="label">Total <IvaTag>IVA incluido</IvaTag></div>
              </div>
              <p className="caption">
                Cuenta las ventas <strong>no anuladas</strong> de los filtros de arriba. Las fechas son las de la{' '}
                <strong>venta</strong> (cuándo se cobró, hora de México): lo que vendiste hoy entra en «Hoy» aunque
                la actividad sea otro día.
              </p>
            </SummaryGrid>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              {loading ? 'Calculando…' : 'Sin datos.'}
            </p>
          )}
        </Panel>

        {error && (
          <ErrorMessage role="alert">
            {error}{' '}
            <button type="button" onClick={load} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
              Reintentar
            </button>
          </ErrorMessage>
        )}

        {loading && !data ? (
          <Loading text="Cargando ventas..." />
        ) : sales.length === 0 && !error ? (
          <EmptyBox>
            <h3>No hay ventas con estos filtros</h3>
            <p>Cambia las fechas o el estado, o registra una venta nueva.</p>
          </EmptyBox>
        ) : (
          <>
            <SaleList aria-busy={loading}>
              {sales.map((sale) => {
                const state = saleStatus(sale);
                return (
                  <SaleRow
                    key={sale.id}
                    type="button"
                    data-voided={isVoided(sale)}
                    onClick={() => setSelected(sale)}
                    aria-label={`Ticket ${sale.ticketNumber}, ${sale.activityName}, ${state.label}. Ver detalle`}
                  >
                    <span className="ticket">#{sale.ticketNumber}</span>
                    <span className="main">
                      <div className="title">{sale.activityName}</div>
                      <div className="meta">
                        {formatCalendarDate(sale.date)} · {sale.time} · {sale.quantity} {sale.quantity === 1 ? 'lugar' : 'lugares'}
                        {' · '}{paymentMethodLabel(sale.paymentMethod)}
                      </div>
                      {!isStaff && sale.soldBy && <div className="meta">Vendió: {sale.soldBy}</div>}
                      {sale.promoter && <div className="meta">Promotor: {sale.promoter.name}</div>}
                    </span>
                    <span className="side">
                      <span className="amount">{formatMoney(sale.finalAmount)}</span>
                      <StatusBadge status={state.status}>{state.label}</StatusBadge>
                    </span>
                  </SaleRow>
                );
              })}
            </SaleList>

            {totalPages > 1 && (
              <Pager>
                <Button size="small" variant="ghost" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                  ← Anteriores
                </Button>
                <span>Página {page} de {totalPages} · {data.total} ventas</span>
                <Button size="small" variant="ghost" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>
                  Siguientes →
                </Button>
              </Pager>
            )}
          </>
        )}
      </SalesContainer>

      {selected && (
        <SaleDetailModal
          mode={mode}
          sale={selected}
          canVoid={!isStaff}
          onClose={() => setSelected(null)}
          onChanged={handleChanged}
          onSessionError={handleSessionError}
        />
      )}
      {exporting && (
        <ExportModal
          activities={activities}
          defaultActivityId={filters.activityId}
          onClose={() => setExporting(false)}
        />
      )}
    </Layout>
  );
};

const SalesList = ({ mode = 'empresa' }) => {
  const access = useSalesAccess(mode);
  const gate = SalesGate({ access });
  if (gate) return gate;
  return <SalesListScreen access={access} />;
};

export default SalesList;
